import { notify } from "@portal/BaseComponents/Notifications";
import { portalTabQueryKeys } from "@portal/Components/Portal/PotalTabs/types";
import {
  createADocument,
  deleteDocument,
  getDocumentTypes,
  getDocumentsList,
} from "@portal/api/document";
import { DocumentFilters } from "@portal/store/Filters/documents/type";
import { tableAtom } from "@portal/store/table/table";
import { RequestConfig } from "@portal/types/common";
import {
  CreateDocument,
  DOCUMENT_FILTER,
} from "@portal/types/entities/documents";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import produce from "immer";
import { useSetAtom } from "jotai";

export function useDocumentTypesQuery(filter: DOCUMENT_FILTER) {
  return useQuery({
    queryKey: ["document-types", filter],
    queryFn: () => getDocumentTypes(filter),
    retry: false,
  });
}

export function useCreateADocumentQuery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-a-document"],
    mutationFn: (data: CreateDocument) => createADocument(data),
    onSuccess() {
      queryClient.invalidateQueries([portalTabQueryKeys.DOCUMENTS]);
      notify.success("Document Created Successfully");
    },
    onError() {
      notify.error("Failed to create Document. Please try again later.");
    },
  });
}

export function useDocumentListQuery(params: RequestConfig<DocumentFilters>) {
  const setTableValue = useSetAtom(tableAtom);

  return useQuery({
    queryKey: [portalTabQueryKeys.DOCUMENTS, params],
    queryFn: () => getDocumentsList(params),
    retry: false,
    async onSuccess(data) {
      setTableValue((value) => {
        return produce(value, (draft) => {
          draft.bulk_records = data.total_records;
          return draft;
        });
      });
    },
    onError(err) {},
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-a-document"],
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess() {
      queryClient.invalidateQueries([portalTabQueryKeys.DOCUMENTS]);
      notify.success("Document Deleted Successfully");
    },
    onError() {
      notify.error("Failed to delete Document. Please try again later.");
    },
  });
}
